/*
  CRO colour grouping, Part 1 - swatches on collection cards.
  Quoted separately 11 Sep 2026, 13 hrs. Dev theme only at this stage.

  WHAT IT DOES
  On any collection page, products that are the same style in different colours (MONICA, JUDE)
  collapse to a single card carrying colour swatches. Clicking a swatch swaps the card's photo
  and its link without leaving the page.

  WHY THE DATA LIVES IN A JSON ASSET
  The grouping needs every colour of a style, but a collection page only has the products on
  the current page of results - MONICA alone is 48 products across several pages. Liquid cannot
  see past the current page, so the full map is generated from the catalogue and shipped as
  assets/cro-colour-groups.json. Regenerate it when colours are added or removed.

  For production this would move to product metafields so it stays in sync automatically.
  It is a static asset for now precisely so nothing is written to live product data while this
  is still being reviewed.
*/
(function () {
  'use strict';

  var CARD = '.product-card-item';

  // The data file stores one bare CDN url per product; the size is asked for here so the
  // 533px card image and the 120px swatch are not both written into the asset.
  function sized(url, w) {
    if (!url) return '';
    return url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=' + w;
  }

  function activeColourFilters() {
    return new URLSearchParams(location.search).getAll('filter.p.m.custom.colour')
      .map(function (v) { return String(v).trim().toUpperCase(); })
      .filter(Boolean);
  }

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  function handleFromCard(card) {
    var a = card.querySelector('.product-item-details .title a[href*="/products/"]') ||
            card.querySelector('a[href*="/products/"]');
    if (!a) return null;
    var m = a.getAttribute('href').match(/\/products\/([^/?#]+)/);
    return m ? m[1] : null;
  }

  var STYLE_BY_HANDLE = {};
  var DATA = null;
  var watching = false;

  /* Part 2, approved 22 Sep 2026. The "N colours" caption under the swatches is the way into the
     colour picker page. Jordianne asked for the link to apply inside the site only, so this is a
     plain link on our own pages: anyone arriving from Google still lands on the product page they
     clicked, and every colour keeps its own page.

     PICKER_STYLES is deliberately a short list rather than every grouped style. The picker page
     only holds MONICA and JUDE - CAMILLA is not on it at all - so any other style would be
     linking to a page that does not contain it. Check the page before adding a name here.

     The page also answers on /pages/variant-swatches-page, an older address for the same page.
     Links point at the canonical one. */
  var PICKER_URL = '/pages/headpiece-colour-picker';
  var PICKER_STYLES = ['MONICA', 'JUDE'];

  /* The team chose the single scrolling row with arrows on 17 Sep 2026, so that is the only
     behaviour now. The dropdown version that was built alongside it for comparison - a "+N"
     button opening a panel of every colour - has been removed, along with the ?swatches switch
     that let the two be compared. A stale ?swatches= link in someone's history is harmless: it
     is ignored here and excluded from filterKey below. */

  // Filtered views: the whole result set, fetched once per filter combination from
  // templates/collection.cro-handles.liquid.
  var FILTER_KEY = null, LOADING_KEY = null, FILTER_SET = null, FILTER_ORDER = null;

  function filterKey() {
    // Empty values are dropped: the theme's filter form leaves filter.v.price.gte= and friends in
    // the address after a filter is cleared, and those must not count as filtering. The version
    // switch is not a filter either.
    var p = new URLSearchParams(location.search);
    var clean = new URLSearchParams();
    p.forEach(function (v, k) {
      if (v !== '' && k !== 'page' && k !== 'section_id' && k !== 'view' && k !== 'swatches') clean.append(k, v);
    });
    return clean.toString();
  }

  function filtering() {
    return /(^|&)filter\./.test(filterKey());
  }

  function loadFilterSet() {
    var key = filterKey();
    var p = new URLSearchParams(key);
    p.set('view', 'cro-handles');
    return fetch(location.pathname + '?' + p.toString(), { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (j) { FILTER_ORDER = j.handles || []; FILTER_SET = new Set(FILTER_ORDER); })
      .catch(function () { FILTER_ORDER = null; FILTER_SET = null; })
      .then(function () { FILTER_KEY = key; });
  }

  /* ------------------------------------------------------------------------------------
     FILTERED VIEWS: ONE CARD PER STYLE, WITH REAL PAGES

     A filter can return any mix of products, so no list can be prepared in advance the way the
     unfiltered pages are (snippets/cro-collection-leads.liquid). So, per filter and sort:
       1. fetch the whole result list - handles only, 250 a request - from
          templates/collection.cro-handles.liquid
       2. keep the first product of each style, in result order
       3. show page N of those, reusing cards already on the page and fetching the rest from
          templates/product.cro-card.liquid, which draws the theme's own card
       4. replace the pager with one that counts styles
     The grid stays hidden (html.cro-pending) while this runs so the product-based page never
     flashes. Any failure leaves the theme's grid as it came.
     ------------------------------------------------------------------------------------ */
  var HANDLES_CACHE = {};
  var STYLING = null;

  function pend() {
    document.documentElement.classList.add('cro-pending');
    clearTimeout(pend.t);
    pend.t = setTimeout(unpend, 6000);
  }

  function unpend() {
    document.documentElement.classList.remove('cro-pending');
  }

  function currentPage() {
    var n = parseInt(new URLSearchParams(location.search).get('page'), 10);
    return isNaN(n) || n < 1 ? 1 : n;
  }

  function pageSize() {
    var m = document.getElementById('cro-grid-meta');
    var n = m ? parseInt(m.getAttribute('data-limit'), 10) : NaN;
    return isNaN(n) || n < 1 ? 12 : n;
  }

  // The collection section says whether it already built one-card-per-style pages itself
  // (snippets/cro-collection-leads.liquid). When it did not - most collections, and any filter -
  // the script builds them.
  function serverPagesActive() {
    var m = document.getElementById('cro-grid-meta');
    return !!m && m.getAttribute('data-server-leads') === '1';
  }

  function needsStyleMode() {
    return !!document.getElementById('cro-grid-meta') && (filtering() || !serverPagesActive());
  }

  function styleTag() {
    return filterKey() + '|' + currentPage();
  }

  function productGrid() {
    return document.querySelector('#CollectionProductGrid .gp-products-inner');
  }

  function styleKeyOf(handle) {
    return STYLE_BY_HANDLE[handle] || 'P:' + handle;
  }

  function loadAllHandles(key) {
    if (HANDLES_CACHE[key]) return HANDLES_CACHE[key];
    var all = [];
    function chunk(n) {
      var p = new URLSearchParams(key);
      p.set('view', 'cro-handles');
      p.set('page', n);
      return fetch(location.pathname + '?' + p.toString(), { credentials: 'same-origin' })
        .then(function (r) { return r.json(); })
        .then(function (j) {
          var got = j.handles || [];
          // Titles come back alongside, so a product the bundled file has never heard of still
          // knows which style it belongs to. Without this a newly added colour was its own card.
          var titles = j.titles || [];
          got.forEach(function (h, i) {
            if (STYLE_BY_HANDLE[h]) return;
            var s = styleFromTitle(titles[i]);
            if (s) STYLE_BY_HANDLE[h] = s;
          });
          all = all.concat(got);
          // 250 is the template's page size, so a full chunk means there may be more.
          return got.length === 250 && n < 40 ? chunk(n + 1) : all;
        });
    }
    // The promise is cached, not just its answer, so a second caller joins the request in
    // flight instead of starting another one. A failed read drops out of the cache so it
    // can be tried again rather than failing for the life of the page.
    HANDLES_CACHE[key] = chunk(1).catch(function (e) {
      delete HANDLES_CACHE[key];
      throw e;
    });
    return HANDLES_CACHE[key];
  }

  function fetchCard(handle) {
    var base = (location.pathname.match(/^.*\/collections\/[^/]+/) || [''])[0];
    return fetch(base + '/products/' + encodeURIComponent(handle) + '?view=cro-card', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.text() : ''; })
      .then(function (html) {
        var box = document.createElement('div');
        box.innerHTML = html;
        return box.querySelector('.col');
      })
      .catch(function () { return null; });
  }

  function resetCol(col) {
    col.hidden = false;
    col.removeAttribute('data-cro-grouped-away');
    [].slice.call(col.querySelectorAll('.cro-sw, .cro-sw__count')).forEach(function (el) { el.remove(); });
    [].slice.call(col.querySelectorAll(CARD)).forEach(function (c) { c.removeAttribute('data-cro-done'); });
    return col;
  }

  function hrefForPage(n) {
    var q = new URLSearchParams(location.search);
    q.delete('section_id');
    q.delete('view');
    if (n > 1) q.set('page', n); else q.delete('page');
    var s = q.toString();
    return location.pathname + (s ? '?' + s : '');
  }

  // Same markup and classes as snippets/cro-pagination.liquid, so both pagers look identical.
  function renderPager(grid, current, pages, total) {
    var host = grid.parentElement;
    var wrap = host.querySelector('.pagination-wrapper');
    if (pages <= 1) { if (wrap) wrap.remove(); return; }
    if (!wrap) {
      wrap = document.createElement('div');
      wrap.className = 'pagination-wrapper';
      host.appendChild(wrap);
    }
    var nav = document.createElement('nav');
    nav.className = 'pagination';
    nav.setAttribute('role', 'navigation');
    nav.setAttribute('aria-label', 'Pagination, ' + total + ' styles');
    var ul = document.createElement('ul');
    ul.className = 'pagination__list list-unstyled pagination-custom';
    ul.setAttribute('role', 'list');

    function add(text, target, cls, aria) {
      var li = document.createElement('li');
      var el = document.createElement(target ? 'a' : 'span');
      if (target) el.href = hrefForPage(target);
      el.className = 'pagination__item ' + cls;
      if (aria) el.setAttribute('aria-label', aria);
      el.textContent = text;
      li.appendChild(el);
      ul.appendChild(li);
      return el;
    }

    add('‹', current > 1 ? current - 1 : 0,
        'pagination__item--prev pagination__item-arrow link motion-reduce' + (current > 1 ? '' : ' disabled'), 'Previous page');
    for (var i = 1; i <= pages; i++) {
      if (i === 1 || i === pages || (i >= current - 1 && i <= current + 1)) {
        if (i === current) add(String(i), 0, 'pagination__item--current link').setAttribute('aria-current', 'page');
        else add(String(i), i, 'link');
      } else if (i === 2 || i === pages - 1) {
        add('…', 0, 'pagination__item--gap');
      }
    }
    add('›', current < pages ? current + 1 : 0,
        'pagination__item--next pagination__item-arrow link motion-reduce' + (current < pages ? '' : ' disabled'), 'Next page');
    nav.appendChild(ul);
    wrap.replaceChildren(nav);
  }

  function restyleFiltered() {
    var tag = styleTag();
    var grid = productGrid();
    if (!grid) return Promise.resolve(false);
    if (grid.getAttribute('data-cro-styled') === tag) return Promise.resolve(true);
    if (STYLING === tag) return Promise.resolve(false);
    STYLING = tag;
    var key = filterKey();
    return loadAllHandles(key)
      .then(function (handles) {
        FILTER_KEY = key;
        FILTER_ORDER = handles;
        FILTER_SET = new Set(handles);
        if (!handles.length) return false;

        var leads = [], seen = {};
        handles.forEach(function (h) {
          var s = styleKeyOf(h);
          if (!seen[s]) { seen[s] = true; leads.push(h); }
        });

        // Nothing groups in this result set - leave the theme's own grid and pager alone.
        if (leads.length === handles.length) {
          grid.setAttribute('data-cro-styled', tag);
          return true;
        }
        var size = pageSize();
        var pages = Math.max(1, Math.ceil(leads.length / size));
        var page = Math.min(currentPage(), pages);
        var want = leads.slice((page - 1) * size, page * size);

        var onPage = {};
        [].slice.call(grid.querySelectorAll('.col')).forEach(function (col) {
          var c = col.querySelector(CARD);
          var h = c && handleFromCard(c);
          if (h && !onPage[h]) onPage[h] = col;
        });

        /* The grid goes up as soon as the cards already on the page are in their places, and
           the ones that have to be fetched are slotted in as they land.

           It used to wait on Promise.all before touching the grid at all. On RACING & PILLBOX
           HATS that meant the whole grid sat behind the slowest of five card requests - 1.2s
           after the rest - and the customer watched the theme's own grid the entire time, then
           saw it replaced. That is what Chantelle's recording shows: "POPPY in black is the
           first product on the first row that loads but changes to MONICA in black", 14 Oct 2026.

           A card that has not arrived holds its place with an empty hidden column, so the ones
           that have are already in the right order and nothing moves when the rest fill in. */
        if (styleTag() !== tag || productGrid() !== grid) return false;       // the view moved on
        var missing = [];
        var slots = want.map(function (h) {
          if (onPage[h]) return resetCol(onPage[h]);
          missing.push(h);
          var slot = document.createElement('div');
          slot.className = 'col';
          slot.setAttribute('data-cro-slot', h);
          slot.hidden = true;
          return slot;
        });
        if (!slots.length) return false;

        grid.setAttribute('data-cro-styled', tag);
        grid.replaceChildren.apply(grid, slots);
        renderPager(grid, page, pages, leads.length);
        unpend();

        missing.forEach(function (h) {
          fetchCard(h).then(function (col) {
            if (styleTag() !== tag || productGrid() !== grid) return;
            var slot = grid.querySelector('[data-cro-slot="' + h + '"]');
            if (!slot) return;
            // Swatches go on through watchGrid, which is already watching for cards it has not
            // seen. A card that cannot be fetched gives its place up rather than leaving a hole.
            if (col) grid.replaceChild(resetCol(col), slot);
            else slot.remove();
          });
        });
        return true;
      })
      .catch(function () { return false; })
      .then(function (ok) {
        if (STYLING === tag) STYLING = null;
        unpend();
        return ok;
      });
  }

  /* The filter, the sort and the view toggle all re-render the product grid over AJAX. This
     script otherwise ran once at page load, so filtering to a colour produced a fresh grid with
     no swatches on it at all - which is exactly what it looked like: broken.

     Cards are stamped data-cro-done once handled, so a re-run only touches new ones and our own
     DOM changes cannot retrigger the pass. */
  function watchGrid() {
    if (watching) return;
    var host = document.querySelector('#CollectionProductGrid') ||
               document.querySelector('.gp-products');
    if (!host) return;
    watching = true;
    var timer = null;
    new MutationObserver(function () {
      // Hide a freshly filtered grid in the same frame it arrives, before it can paint.
      if (DATA && needsStyleMode() && host.querySelector(CARD + ':not([data-cro-done])')) {
        var g = productGrid();
        if (g && g.getAttribute('data-cro-styled') !== styleTag()) pend();
      }
      clearTimeout(timer);
      timer = setTimeout(function () {
        if (!DATA) return;
        if (host.querySelector(CARD + ':not([data-cro-done])')) build(DATA);
      }, 120);
    }).observe(host, { childList: true, subtree: true });
  }

  /* The style is the product name up to the bracket: "MONICA (navy polka)" -> MONICA. Deriving it
     from the title is what makes grouping work for products nobody has told us about.

     assets/cro-colour-groups.json only knows the products that existed when it was generated, so
     anything added since sat on its own: MONICA (metallic rose gold) had its own card with no
     swatches, and CAMILLA white and black never joined CAMILLA gold - both reported by the client
     on 1 Oct 2026, with "we're really hoping that this would be automated".

     A name with no bracket is its own style, which is right: those are one-off pieces. */
  function styleFromTitle(title) {
    var t = String(title || '').trim();
    if (!t) return null;
    var i = t.indexOf('(');
    return (i > 0 ? t.slice(0, i) : t).trim().toUpperCase() || null;
  }

  // The product name as the card shows it, used to work out the style of anything on the page.
  function titleFromCard(card) {
    var a = card.querySelector('.product-item-details .title a[href*="/products/"]');
    return a ? a.textContent : null;
  }

  // Everything on the page right now, whether or not the bundled file has heard of it.
  function learnStylesFromDom(index) {
    [].slice.call(document.querySelectorAll('.col')).forEach(function (col) {
      var handle = handleFromCard(col);
      if (!handle || index[handle]) return;
      var style = styleFromTitle(titleFromCard(col));
      if (!style) return;
      index[handle] = { style: style, i: 0 };
      STYLE_BY_HANDLE[handle] = style;
    });
  }

  function indexData(data) {
    var index = {};
    Object.keys(data.styles).forEach(function (style) {
      data.styles[style].forEach(function (item, i) {
        index[item.h] = { style: style, i: i };
        STYLE_BY_HANDLE[item.h] = style;
      });
    });
    learnStylesFromDom(index);
    return index;
  }

  function build(data, afterRestyle) {
    var index = indexData(data);

    if (needsStyleMode() && !afterRestyle) {
      var styledGrid = productGrid();
      if (styledGrid && styledGrid.getAttribute('data-cro-styled') !== styleTag()) {
        pend();
        restyleFiltered().then(function () { build(data, true); });
        return;
      }
    }
    if (!filtering()) unpend();

    if (filtering() && FILTER_KEY !== filterKey()) {
      if (LOADING_KEY !== filterKey()) {
        LOADING_KEY = filterKey();
        loadFilterSet().then(function () { LOADING_KEY = null; build(data, true); });
      }
      return;
    }
    if (!filtering()) { FILTER_KEY = null; FILTER_SET = null; FILTER_ORDER = null; }

    // Iterate COLUMNS, not cards. The theme renders two .product-card-item per product inside
    // one .col - a grid-mode card and a list-mode card, with CSS showing whichever the view
    // toggle selects. Walking cards meant the second copy of the first product saw its own
    // style already marked as seen and hid the column it was sitting in, taking the kept card
    // with it. Verified on /collections/monica: 24 cards, 12 columns.
    var firstOfStyle = {};
    if (FILTER_ORDER) {
      FILTER_ORDER.forEach(function (h) {
        var s = STYLE_BY_HANDLE[h];
        if (s && !firstOfStyle[s]) firstOfStyle[s] = h;
      });
    }

    var seen = {};
    var cols = [];
    Array.prototype.forEach.call(document.querySelectorAll(CARD), function (card) {
      var col = card.closest('.col') || card;
      if (cols.indexOf(col) === -1) cols.push(col);
    });

    cols.forEach(function (col) {
      var cards = col.querySelectorAll(CARD);
      if (!cards.length) return;
      var handle = handleFromCard(cards[0]);
      if (!handle) return;
      var hit = index[handle];
      // Ungrouped products are tracked too, so a top-up page cannot re-add one.
      if (!hit) {
        seen['product:' + handle] = true;
        Array.prototype.forEach.call(cards, function (c) { c.setAttribute('data-cro-done', '1'); });
        return;
      }

      // One column per style. The rest of that style's columns come out of the grid, which is
      // the whole point - 48 MONICA products become one card.
      // Filtered, a style's matching colours can sit on different pages. Only the first in the
      // whole result set keeps a card - MONICA black and polka make one card, not one per page.
      if (FILTER_ORDER && firstOfStyle[hit.style] && firstOfStyle[hit.style] !== handle) {
        col.setAttribute('data-cro-grouped-away', hit.style);
        col.hidden = true;
        Array.prototype.forEach.call(cards, function (c) { c.setAttribute('data-cro-done', '1'); });
        return;
      }

      if (seen[hit.style]) {
        col.setAttribute('data-cro-grouped-away', hit.style);
        col.hidden = true;
        Array.prototype.forEach.call(cards, function (c) { c.setAttribute('data-cro-done', '1'); });
        return;
      }
      seen[hit.style] = true;
      // Both view modes get swatches, so switching grid/list does not lose them.
      // A style learned from the page itself has no colours in the bundled file yet, and asking
      // render to draw a row from nothing threw - which the promise chain swallowed, so the whole
      // grid came back with no swatches at all. Those styles get their row from the live read a
      // moment later instead.
      Array.prototype.forEach.call(cards, function (c) {
        if (data.styles[hit.style]) render(c, data.styles[hit.style], handle);
        c.setAttribute('data-cro-done', '1');
      });
    });

    // Nothing collapsed means nothing to top up - leave the theme's own grid and pager alone.
    var removed = document.querySelectorAll('[data-cro-grouped-away]').length;
    if (!removed) return;

    // Same when the customer has narrowed things themselves. Shopify paginates the filtered set
    // server-side and that count is already correct, so topping up would overfill the page and
    // removing the pager would throw away a pager that was right. Grouping still applies - the
    // duplicates within the filtered set are still collapsed - but the page is left as it came.
    if (/[?&](filter\.|sort_by=|q=)/.test(location.search)) return;

    // Inside the collection section, pages come from the server list or restyleFiltered(). The
    // Load more fallback below only applies to grids outside it - it left Polo & Picnics with
    // eleven cards, no page numbers and no way on.
    if (document.getElementById('cro-grid-meta')) return;

    GRID = document.querySelector('.gp-products-inner');
    var host = GRID && GRID.closest('[id^="shopify-section-"]');
    if (!GRID || !host) return;
    SECTION_ID = host.id.replace('shopify-section-', '');
    seenStyles = seen;
    target = cols.length || 12;
    readPager();

    // The numbered pager counts products; the grid now counts styles. Showing "35 pages" beside
    // six cards is the thing that looks broken, so it is replaced with Load more.
    var list = document.querySelector('.pagination__list');
    if (list) list.remove();

    topUp(index, data, target);

    var wrap = document.querySelector('.pagination-wrapper');
    if (wrap) {
      wrap.addEventListener('click', function (ev) {
        var b = ev.target.closest('.cro-more');
        if (!b || busy) return;
        b.disabled = true;
        b.textContent = 'Loading';
        topUp(index, data, visibleCount() + target);
      });
    }
  }

  function render(card, colours, currentHandle) {
    var details = card.querySelector('.product-item-details');
    if (!details || details.querySelector('.cro-sw')) return;

    // The card shows the colour it is displaying, starting on the lead colour and following
    // whichever swatch is picked. Client's call, 12 Sep 2026. The approved mock instead titled
    // the card with the bare style name and never changed it; this is a deliberate departure.

    // Every colour, no cap. The mock showed all of them and captioned the row "N colours";
    // there was no "+N" control in what was approved.
    var ordered = colours.slice();
    var idx = ordered.findIndex(function (c) { return c.h === currentHandle; });
    if (idx > 0) ordered.unshift(ordered.splice(idx, 1)[0]);

    // With a colour filter on, offer only the colours that match it, as the ungrouped listing
    // did: BLACK gives MONICA black and polka, not all 48. One match means nothing to choose.
    if (FILTER_SET) {
      ordered = ordered.filter(function (c) { return c.h === currentHandle || FILTER_SET.has(c.h); });
      if (ordered.length < 2) return;
    } else {
      var active = activeColourFilters();
      if (active.length) {
        ordered = ordered.filter(function (c) {
          return c.h === currentHandle || (c.k || []).some(function (tag) { return active.indexOf(tag) > -1; });
        });
        if (ordered.length < 2) return;
      }
    }

    /* A style can come back with a single colour once the live read has had its say - the others
       archived or unpublished since the bundled file was made. One swatch is nothing to choose
       between, and the caption read "1 colours", so no row is drawn at all. */
    if (ordered.length < 2) return;

    var wrap = document.createElement('div');
    wrap.className = 'cro-sw';
    wrap.setAttribute('role', 'group');
    wrap.setAttribute('aria-label', 'Colours available');

    var track = document.createElement('div');
    track.className = 'cro-sw__track';

    function dot(c) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'cro-sw__dot';
      b.setAttribute('aria-label', c.l);
      b.setAttribute('title', c.l);
      if (c.h === currentHandle) b.setAttribute('aria-current', 'true');
      paint(b, c);
      b.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        select(card, wrap, c);
      });
      return b;
    }

    ordered.forEach(function (c) { track.appendChild(dot(c)); });

    // One row that scrolls sideways - swipe on touch, arrows either side. Chosen by the team on
    // 17 Sep 2026 over the dropdown version that was built alongside it. MONICA and JUDE are each
    // heading for 100 colours, which wrapped into a block taller than the photo.
    var prev = arrow('prev', 'Previous colours');
    var next = arrow('next', 'More colours');
    prev.addEventListener('click', function (e) { e.preventDefault(); e.stopPropagation(); slide(track, -1); });
    next.addEventListener('click', function (e) { e.preventDefault(); e.stopPropagation(); slide(track, 1); });
    track.addEventListener('scroll', function () { syncArrows(wrap, track); }, { passive: true });

    wrap.appendChild(prev);
    wrap.appendChild(track);
    wrap.appendChild(next);

    // The caption doubles as the entry point to the picker page, for the styles that page holds.
    // Everything else keeps a plain caption. The count is the style's full colour count, not the
    // filtered one, so a filtered view still says how many there are altogether.
    var style = styleOf(currentHandle);
    var count;
    if (PICKER_STYLES.indexOf(style) > -1) {
      count = document.createElement('a');
      count.setAttribute('href', PICKER_URL);
      count.setAttribute('aria-label', 'See all ' + colours.length + ' ' + style + ' colours');
      // The card's own product link sits in the same details box; without this a tap on the
      // caption can be taken by the card instead of the link.
      count.addEventListener('click', function (e) { e.stopPropagation(); });
    } else {
      count = document.createElement('span');
    }
    count.className = 'cro-sw__count';
    count.textContent = ordered.length + (ordered.length === 1 ? ' colour' : ' colours');

    details.appendChild(wrap);
    details.appendChild(count);

    // Widths are only real once the row is on the page, and they change with the grid/list
    // toggle. The wrapper is watched rather than the track, because showing the arrows resizes
    // the track itself.
    syncArrows(wrap, track);
    if (window.ResizeObserver) new ResizeObserver(function () { syncArrows(wrap, track); }).observe(wrap);
  }

  /* Colour swatches, not photo thumbnails - Jordianne asked for this on 31 Aug 2026, after the
     28 Aug mock, and the build wrongly followed the mock's photos until 15 Sep.
     The colour is the "c" list in assets/cro-colour-groups.json: Shopify's own colour value for
     the product, or a swatch of the same name. Two values make a split circle (black & white).
     Prints and patterns have no single colour, so those show a close-up of the product photo. */
  /* A polka colour is its base with white spots, not a flat circle - the client's point on
     28 Sep 2026 about JUDE (navy polka): "it's polka, which is white then the colour which is
     navy blue". The spots are drawn rather than cropped from the photo, so they read at 20px.
     Two tiled radial gradients, offset by half a tile, give an even scatter. */
  function polka(dot, base) {
    dot.style.backgroundColor = base;
    // Four spots, placed rather than tiled. Tiling filled the circle with a dozen specks that
    // read as noise at 20px; four sit clearly apart and still say polka. Placed slightly off
    // a grid so they look scattered, and in percentages so they scale with the swatch.
    dot.style.backgroundImage =
      'radial-gradient(circle at 31% 29%, #fff 10%, transparent 11%),' +
      'radial-gradient(circle at 71% 33%, #fff 10%, transparent 11%),' +
      'radial-gradient(circle at 29% 70%, #fff 10%, transparent 11%),' +
      'radial-gradient(circle at 69% 72%, #fff 10%, transparent 11%)';
    dot.style.backgroundSize = '100% 100%';
    dot.style.backgroundRepeat = 'no-repeat';
  }

  function paint(dot, colour) {
    var cols = colour.c || [];
    if (colour.pt === 'polka' && cols.length) { polka(dot, cols[0]); return; }
    if (cols.length > 1) {
      dot.style.backgroundImage = 'linear-gradient(135deg, ' + cols[0] + ' 50%, ' + cols[1] + ' 50%)';
      dot.style.backgroundSize = '100% 100%';
    } else if (cols.length === 1) {
      dot.style.backgroundColor = cols[0];
    } else if (colour.i) {
      dot.style.backgroundImage = 'url("' + sized(colour.i, 160) + '")';
    }
  }

  var CHEVRON = '<svg width="7" height="12" viewBox="0 0 7 12" aria-hidden="true" focusable="false">' +
                '<path d="M1 1l5 5-5 5" fill="none" stroke="currentColor" stroke-width="1.4"/></svg>';

  function arrow(dir, label) {
    var b = document.createElement('button');
    b.type = 'button';
    b.className = 'cro-sw__arrow cro-sw__arrow--' + dir;
    b.setAttribute('aria-label', label);
    b.innerHTML = CHEVRON;
    return b;
  }

  // Arrows dim at either end. The row is re-fitted only when the card changes width, not on
  // every scroll.
  function syncArrows(wrap, track) {
    var w = wrap.clientWidth;
    if (wrap.croWidth !== w) { wrap.croWidth = w; fitRow(wrap, track); }
    var max = track.scrollWidth - track.clientWidth;
    wrap.querySelector('.cro-sw__arrow--prev').disabled = max <= 1 || track.scrollLeft <= 1;
    wrap.querySelector('.cro-sw__arrow--next').disabled = max <= 1 || track.scrollLeft >= max - 1;
  }

  // Arrows only when the colours don't all fit. When they don't, the row is trimmed to whole
  // swatches: a half-shown black circle at the edge reads as a black-and-white swatch.
  function fitRow(wrap, track) {
    track.style.maxWidth = '';
    wrap.classList.remove('cro-sw--scroll');
    if (track.scrollWidth - track.clientWidth <= 1) return;
    wrap.classList.add('cro-sw--scroll');
    var m = rowMetrics(track);
    track.style.maxWidth = (m.fit * m.step - m.gap + m.pad) + 'px';
  }

  function rowMetrics(track) {
    var cs = getComputedStyle(track);
    var gap = parseFloat(cs.columnGap) || 0;
    var pad = (parseFloat(cs.paddingLeft) || 0) + (parseFloat(cs.paddingRight) || 0);
    var dot = track.querySelector('.cro-sw__dot');
    var step = (dot ? dot.offsetWidth : 20) + gap;
    return { gap: gap, pad: pad, step: step, fit: Math.max(1, Math.floor((track.clientWidth - pad + gap) / step)) };
  }

  // A row's worth at a time, less one swatch so the last colour seen stays as a reference.
  function slide(track, dir) {
    var m = rowMetrics(track);
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    track.scrollBy({ left: dir * Math.max(1, m.fit - 1) * m.step, behavior: reduce ? 'auto' : 'smooth' });
  }

  /* ------------------------------------------------------------------------------------
     TOPPING THE GRID BACK UP

     Grouping removes cards, so a page built for 12 products can end up showing 6. Shopify
     paginates server-side by PRODUCT before any of this runs, so the page count is decided
     before we see it - on Spring Racing that is 411 live products at 12 a page, or 35 pages
     of increasingly few cards.

     So after grouping we pull the next page's grid from the Section Rendering API, keep only
     the styles not already on screen, and append until the grid is full again. The numbered
     pager is then replaced with a Load more that does the same thing, because the numbers
     describe products and the grid now shows styles - they cannot agree.

     This is the theme-side answer. Removing the extra colourways from the collections is the
     tidier one and makes the numbers honest at source, but it is a catalogue change and needs
     signing off; this needs nothing but the theme.
     ------------------------------------------------------------------------------------ */

  var SECTION_ID = null;
  var GRID = null;
  var nextPage = 2;
  var lastPage = 1;
  var target = 12;
  var seenStyles = null;
  var busy = false;

  function readPager() {
    var nums = [].slice.call(document.querySelectorAll('.pagination__list a, .pagination__list span'))
      .map(function (e) { return parseInt(e.textContent.trim(), 10); })
      .filter(function (n) { return !isNaN(n); });
    lastPage = nums.length ? Math.max.apply(null, nums) : 1;
    var cur = parseInt((location.search.match(/[?&]page=(\d+)/) || [])[1], 10);
    nextPage = (isNaN(cur) ? 1 : cur) + 1;
  }

  function visibleCount() {
    return GRID ? GRID.querySelectorAll('.col:not([data-cro-grouped-away])').length : 0;
  }

  function fetchPage(n) {
    // Carry the existing query string through. Building the URL from the path alone dropped any
    // active filter, so the top-up was pulling unfiltered products into a filtered grid.
    var params = new URLSearchParams(location.search);
    params.set('section_id', SECTION_ID);
    params.set('page', n);
    var url = location.pathname + '?' + params.toString();
    return fetch(url).then(function (r) { return r.ok ? r.text() : ''; }).catch(function () { return ''; });
  }

  // Pull pages until the grid is full again, or there is nothing left that is not already shown.
  function topUp(index, data, wanted) {
    if (busy) return Promise.resolve();
    busy = true;
    var fetches = 0;
    function step() {
      if (visibleCount() >= wanted || nextPage > lastPage || fetches >= 8) {
        busy = false;
        updateMoreButton();
        return Promise.resolve();
      }
      fetches++;
      var n = nextPage++;
      return fetchPage(n).then(function (html) {
        if (!html) return step();
        var box = document.createElement('div');
        box.innerHTML = html;
        [].slice.call(box.querySelectorAll('.col')).forEach(function (col) {
          if (visibleCount() >= wanted) return;
          var cards = col.querySelectorAll(CARD);
          if (!cards.length) return;
          var handle = handleFromCard(cards[0]);
          var hit = handle ? index[handle] : null;
          var key = hit ? hit.style : 'product:' + handle;
          if (seenStyles[key]) return;         // already on screen, in any form
          seenStyles[key] = true;
          GRID.appendChild(col);
          if (hit) {
            [].slice.call(col.querySelectorAll(CARD)).forEach(function (c) {
              if (data.styles[hit.style]) render(c, data.styles[hit.style], handle);
            });
          }
        });
        return step();
      });
    }
    return step();
  }

  function updateMoreButton() {
    var wrap = document.querySelector('.pagination-wrapper');
    if (!wrap) return;
    var btn = wrap.querySelector('.cro-more');
    var done = nextPage > lastPage;
    if (done) { if (btn) btn.remove(); return; }
    if (!btn) {
      btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'btn cro-more';
      btn.textContent = 'Load more';
      wrap.appendChild(btn);
    }
    btn.disabled = false;
    btn.textContent = 'Load more';
  }

  function styleOf(handle) {
    return STYLE_BY_HANDLE[handle] || null;
  }

  function select(card, wrap, colour) {
    var imgs = card.querySelectorAll('.product-media-wrapper img');
    // Both images, not just the first. The second is the hover image - left on the previous
    // colour it showed a different colour to the one just picked.
    Array.prototype.forEach.call(imgs, function (img) {
      if (!colour.i) return;
      img.removeAttribute('srcset');
      img.setAttribute('src', sized(colour.i, 533));
      img.setAttribute('alt', colour.l);
    });

    // Link and title both follow the swatch.
    card.querySelectorAll('a[href*="/products/"]').forEach(function (a) {
      a.setAttribute('href', '/products/' + colour.h);
    });

    var titleLink = card.querySelector('.product-item-details .title a[href*="/products/"]');
    if (titleLink && colour.n) {
      titleLink.textContent = colour.n;
      titleLink.setAttribute('title', colour.n);
    }

    wrap.querySelectorAll('.cro-sw__dot').forEach(function (d) {
      if (d.getAttribute('aria-label') === colour.l) d.setAttribute('aria-current', 'true');
      else d.removeAttribute('aria-current');
    });
  }

  /* ----------------------------------------------------------------------------------------
     Live refresh. Approved 24 Sep 2026 (2.5 hrs, $50). Trello: https://trello.com/c/R8TssrYR

     Jordianne: "I think we do it automatically. Cos I might forget to reach out as well when we
     have a new product with different colours."

     assets/cro-colour-groups.json stays as the first paint: it is one small file, already
     cached, and it puts the swatches up immediately. Straight after that, each style on the page
     is re-read from the catalogue through templates/search.cro-colours.liquid and the row is
     redrawn if anything has changed. So a new colour needs nobody to regenerate anything - it
     appears as soon as the product is set to active, which is also why drafts stay hidden.

     It was already out of date when this was written: MONICA had gained monica-silver and
     monica-metallic-rose-gold-headband and lost monica-headband-lime, and JUDE had gained
     jude-moss-headband.

     Answers are kept for the tab's lifetime, so moving between collection pages re-reads
     nothing. A failure anywhere here leaves the bundled data exactly as it was.
  ---------------------------------------------------------------------------------------- */
  var LIVE_MAX_PAGES = 6;      // 50 per page; JUDE is the largest style at 3
  var LIVE_CONCURRENCY = 3;

  function liveCacheGet(style) {
    try {
      var raw = sessionStorage.getItem('cro_live_' + style);
      return raw ? JSON.parse(raw) : null;
    } catch (e) { return null; }
  }

  function liveCacheSet(style, items) {
    try { sessionStorage.setItem('cro_live_' + style, JSON.stringify(items)); } catch (e) {}
  }

  // "MONICA (apricot)" -> "apricot". The bundled file's labels are the bracket, lower case, and
  // the swatch's aria-label and the selected-dot check both match on it.
  function labelFromTitle(title, style) {
    var m = String(title || '').match(/\(([^)]*)\)\s*$/);
    if (m) return m[1].trim().toLowerCase();
    return String(title || '').replace(style, '').trim().toLowerCase();
  }

  function fetchStylePage(style, page) {
    var qs = 'q=' + encodeURIComponent('title:"' + style + '"') +
             '&type=product&view=cro-colours&page=' + page;
    return fetch('/search?' + qs, { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function () { return null; });
  }

  function fetchStyle(style) {
    var cached = liveCacheGet(style);
    if (cached) return Promise.resolve(cached);

    var all = [];
    function page(n) {
      return fetchStylePage(style, n).then(function (d) {
        if (!d || !d.items) return null;
        all = all.concat(d.items);
        if (n < d.pages && n < LIVE_MAX_PAGES) return page(n + 1);
        return all;
      });
    }
    return page(1).then(function (items) {
      if (!items) return null;
      // The search matches the words, so a different style whose name contains this one can come
      // back too. Only "STYLE (colour)" belongs to this style.
      var head = style.toUpperCase() + ' (';
      var mine = items.filter(function (it) {
        return String(it.n || '').toUpperCase().indexOf(head) === 0;
      });
      if (!mine.length) return null;
      // Not every product has a Shopify colour swatch set - 6 of MONICA's 49 have none. The
      // bundled file has a hex for some of those, so keep it rather than dropping the colour
      // back to a close-up of the photo: MONICA (white) turned grey that way.
      var known = {};
      ((DATA && DATA.styles && DATA.styles[style]) || []).forEach(function (o) { known[o.h] = o; });
      mine.forEach(function (it) {
        var was = known[it.h];
        it.l = labelFromTitle(it.n, style);
        if ((!it.c || !it.c.length) && was && was.c && was.c.length) it.c = was.c;
        if ((!it.k || !it.k.length) && was && was.k && was.k.length) it.k = was.k;
        if (!it.i && was && was.i) it.i = was.i;
      });
      mine.sort(function (a, b) { return a.l < b.l ? -1 : a.l > b.l ? 1 : 0; });
      liveCacheSet(style, mine);
      return mine;
    });
  }

  /* The live read is the catalogue as it is now; the bundled file is a snapshot of it. The
     redraw used to be skipped whenever the two held the same handles in the same order, which
     kept the file's colours even where the live read had better ones - a product with no hex in
     the file stayed a close-up of its photo although the live fallback chain resolves it. So the
     colours count as a difference too. */
  function sameItems(a, b) {
    if (!a || !b || a.length !== b.length) return false;
    for (var i = 0; i < a.length; i++) {
      if (a[i].h !== b[i].h) return false;
      if ((a[i].c || []).join(',') !== (b[i].c || []).join(',')) return false;
      if ((a[i].pt || null) !== (b[i].pt || null)) return false;
    }
    return true;
  }

  // Styles with a swatch row on the page right now, each with the card it was drawn on.
  function stylesOnPage() {
    var out = {};
    [].slice.call(document.querySelectorAll('.col:not([data-cro-grouped-away])')).forEach(function (col) {
      var h = handleFromCard(col);
      if (!h) return;
      // Not only cards that already have a row. A style the bundled file has never heard of has
      // no row to begin with, and that is exactly the one that needs reading from the catalogue -
      // CAMILLA, once white and black were set active alongside gold.
      var s = styleOf(h) || styleFromTitle(titleFromCard(col));
      if (s && !out[s]) out[s] = { card: col, handle: h };
    });
    return out;
  }

  function redraw(style, where, items) {
    DATA.styles[style] = items;
    items.forEach(function (it) { STYLE_BY_HANDLE[it.h] = style; });
    // Only the card still showing this style, and only if the handle it is on still exists -
    // if the colour on the card has been deleted, leave the theme's own card alone.
    var stillThere = items.some(function (it) { return it.h === where.handle; });
    if (!stillThere) return;
    [].slice.call(where.card.querySelectorAll('.cro-sw, .cro-sw__count')).forEach(function (el) {
      el.remove();
    });
    // Both view modes, the same as build does. The column holds a grid card and a list card, and
    // rebuilding only the first left the list one with no swatches after a refresh.
    Array.prototype.forEach.call(where.card.querySelectorAll(CARD), function (c) {
      render(c, items, where.handle);
    });
  }

  function liveRefresh() {
    if (!DATA || !DATA.styles) return;
    var found = stylesOnPage();
    var names = Object.keys(found);
    if (!names.length) return;

    var i = 0;
    function next() {
      if (i >= names.length) return Promise.resolve();
      var style = names[i++];
      return fetchStyle(style).then(function (items) {
        if (items && !sameItems(items, DATA.styles[style])) redraw(style, found[style], items);
      }).catch(function () {}).then(next);
    }
    var lanes = [];
    for (var n = 0; n < LIVE_CONCURRENCY; n++) lanes.push(next());
    return Promise.all(lanes);
  }

  /* The result list is what the grid is held back waiting for, and it has no reason to wait
     for the colour file first. This script is deferred, so the page is parsed by the time it
     runs and the read can start here - about 400ms earlier than it used to. */
  if (document.getElementById('cro-grid-meta') && needsStyleMode()) {
    loadAllHandles(filterKey()).catch(function () {});
  }

  ready(function () {
    var el = document.getElementById('cro-colour-groups');
    if (!el) return;
    fetch(el.getAttribute('data-src'))
      .then(function (r) { return r.json(); })
      .then(function (data) {
        DATA = data;
        build(data);
        watchGrid();
        // After the grid is up, never before it - the bundled data is what makes the first paint
        // instant, and this only corrects it.
        setTimeout(liveRefresh, 0);
      })
      .catch(function () { unpend(); /* leave the grid exactly as the theme rendered it */ });
  });
})();
